import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";

import {
	apiPaths,
	auditLogPageSchema,
	auditSubjectParameter,
	companyIdentitySchema,
	organizationLimitsSchema,
	organizationSecurityStatusSchema,
	teamMemberMfaPath,
	teamMemberPath,
	teamMemberSchema,
	type AddTeamMemberRequest,
	type AuditLogQuery,
	type AuditSubject,
	type CompanyIdentity,
	type OrganizationSecurity,
	type OrganizationSecurityStatus,
	type TeamMember,
} from "@ravnar/api-contract";

import { ApiError, apiRequest } from "./client";
import { LIST_STALE_TIME_MS } from "./refresh";

// The settings pages' requests. Every query key starts with "settings", so a save can refresh every settings query at
// once (each save also adds an audit log entry).

/**
 * How often the newest page of the audit log asks again. An entry reaches the table through a queue, a moment after
 * the action that recorded it, so the page that is open when it happens is the one that has to ask.
 */
export const AUDIT_LOG_REFETCH_INTERVAL_MS = 10_000;

export const companyIdentityQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "identity"],
		queryFn: ({ signal }) => apiRequest(apiPaths.companyIdentity, companyIdentitySchema, { signal }),
	});

export const teamMembersQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "members"],
		queryFn: ({ signal }) => apiRequest(apiPaths.teamMembers, z.array(teamMemberSchema), { signal }),
		// A list a few seconds old is asked again when the page is opened again or the window is come back to.
		staleTime: LIST_STALE_TIME_MS,
	});

/** The organization's security rules and how many members are ready for them. Only owners and admins may read it. */
export const organizationSecurityQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "security"],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.organizationSecurity, organizationSecurityStatusSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
	});

/**
 * Where the organization stands against its limits: what its documents take of its storage, what its agents have spent
 * of this month's AI tokens and when the count starts again, and how many documents wait for tokens. Any member may read
 * it; Settings › Limits shows it, and so does Intake's uploader once a limit is reached.
 */
export const organizationLimitsQueryOptions = () =>
	queryOptions({
		queryKey: ["settings", "limits"],
		queryFn: ({ signal }) => apiRequest(apiPaths.organizationLimits, organizationLimitsSchema, { signal }),
		staleTime: LIST_STALE_TIME_MS,
	});

/**
 * A page of the audit log, newest first, of one section, of what was recorded about one subject ("document:<file id>"),
 * or of all: the newest page unless `page` holds a link from an earlier page. Only owners and admins may read it; the
 * API answers members with 403 and a page link it did not make with 400.
 */
export const auditLogQueryOptions = ({ section, subject, page }: AuditLogQuery = {}) =>
	queryOptions({
		queryKey: ["settings", "audit-log", { section, subject, page }],
		queryFn: ({ signal }) =>
			apiRequest(apiPaths.auditLog, auditLogPageSchema, {
				query: {
					...(section !== undefined && { section }),
					...(subject !== undefined && { subject }),
					...(page !== undefined && { page }),
				},
				signal,
			}),
		staleTime: LIST_STALE_TIME_MS,
		// Only the newest page asks again on its own; a page link deeper in the log names entries that are already
		// written and will not change.
		refetchInterval: page === undefined ? AUDIT_LOG_REFETCH_INTERVAL_MS : false,
		// A refused page link or section will not change on a retry, and the page redirects on it straight away.
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/**
 * What the audit log recorded about one subject — a document's history — newest first, a page at a time: each further
 * page is the one the page before it links to as `next`, and a page parameter of "" is the newest page. The newest page
 * asks again on its own while it is the only one shown, as the Audit log page's does; once older pages are shown, asking
 * again would read every one of them. Only owners and admins may read it; the API answers members with 403.
 */
export const auditTrailQueryOptions = (subject: AuditSubject) =>
	infiniteQueryOptions({
		queryKey: ["settings", "audit-log", "trail", auditSubjectParameter(subject)],
		queryFn: ({ pageParam, signal }) =>
			apiRequest(apiPaths.auditLog, auditLogPageSchema, {
				query: { subject: auditSubjectParameter(subject), ...(pageParam !== "" && { page: pageParam }) },
				signal,
			}),
		initialPageParam: "",
		getNextPageParam: page => page.next ?? undefined,
		staleTime: LIST_STALE_TIME_MS,
		refetchInterval: query => ((query.state.data?.pages.length ?? 1) > 1 ? false : AUDIT_LOG_REFETCH_INTERVAL_MS),
		retry: (failureCount, error) => failureCount < 1 && !(error instanceof ApiError && error.status < 500),
	});

/** Saves the company identity. The API records which fields changed, and nothing when none did. */
export function updateCompanyIdentity(identity: CompanyIdentity): Promise<CompanyIdentity> {
	return apiRequest(apiPaths.companyIdentity, companyIdentitySchema, { method: "PUT", body: identity });
}

/** Saves the organization's security rules. The API records the one value that changed, and nothing when it did not. */
export function updateOrganizationSecurity(security: OrganizationSecurity): Promise<OrganizationSecurityStatus> {
	return apiRequest(apiPaths.organizationSecurity, organizationSecurityStatusSchema, {
		method: "PUT",
		body: security,
	});
}

export interface SaveTeamMemberRequest {
	/** Undefined adds a new member. */
	readonly id: string | undefined;
	readonly member: AddTeamMemberRequest;
}

/**
 * Adds a member, whom the API invites by email with a temporary password, or saves an existing member's name, role and
 * access; an existing member's email cannot change. Fails with 409 for an email this or another organization already
 * has, 404 for a member who is gone, and 403 for a change the signed-in member may not make.
 */
export function saveTeamMember({ id, member }: SaveTeamMemberRequest): Promise<TeamMember> {
	if (id === undefined) {
		return apiRequest(apiPaths.teamMembers, teamMemberSchema, { method: "POST", body: member });
	}
	const { name, role, access } = member;
	return apiRequest(teamMemberPath(id), teamMemberSchema, { method: "PATCH", body: { name, role, access } });
}

/** Removes a member, who loses access straight away. Fails with 404 when they are gone and 403 for yourself. */
export async function removeTeamMember(id: string): Promise<void> {
	await apiRequest(teamMemberPath(id), z.undefined(), { method: "DELETE" });
}

/**
 * Turns off a member's authenticator app, for someone who lost the device it was on. Fails with 404 when they are
 * gone, 409 when they do not use two-step verification, and 403 for a member you may not change.
 */
export async function resetTeamMemberMfa(id: string): Promise<void> {
	await apiRequest(teamMemberMfaPath(id), z.undefined(), { method: "DELETE" });
}
